<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useUserSearch } from '@/js/core/composables/useUserSearch';

const props = defineProps({
  inputId: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: 'Add a user',
  },
  placeholder: {
    type: String,
    default: 'Search by name or email...',
  },
  excludedUuids: {
    type: Array,
    default: () => [],
  },
  error: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select']);

const { searchQuery, searchResults, searching, handleSearch, clearSearch } = useUserSearch(
  () => props.excludedUuids
);

const activeIndex = ref(-1);
const listboxId = computed(() => `${props.inputId}-results`);
const statusId = computed(() => `${props.inputId}-status`);
const errorId = computed(() => `${props.inputId}-error`);
const hasResults = computed(() => searchResults.value.length > 0);
const showNoResults = computed(
  () => searchQuery.value.length >= 2 && !searchResults.value.length && !searching.value
);
const hasPopup = computed(() => hasResults.value || searching.value || showNoResults.value);
const popupId = computed(() => {
  if (hasResults.value) {
    return listboxId.value;
  }

  if (searching.value || showNoResults.value) {
    return statusId.value;
  }

  return undefined;
});
const optionId = index => `${props.inputId}-option-${index}`;
const activeOptionId = computed(() =>
  activeIndex.value >= 0 ? optionId(activeIndex.value) : undefined
);
const describedBy = computed(() =>
  props.error ? `${statusId.value} ${errorId.value}` : statusId.value
);
const optionRefs = ref([]);

const setOptionRef = (element, index) => {
  if (element) {
    optionRefs.value[index] = element;
  }
};

const scrollActiveOptionIntoView = () => {
  nextTick(() => {
    optionRefs.value[activeIndex.value]?.scrollIntoView({ block: 'nearest' });
  });
};

const handleSelectUser = user => {
  emit('select', user);
  activeIndex.value = -1;
  clearSearch();
};

const handleInput = () => {
  activeIndex.value = -1;
  handleSearch();
};

const moveActive = direction => {
  if (!hasResults.value) {
    return;
  }

  const nextIndex = activeIndex.value + direction;
  if (nextIndex < 0) {
    activeIndex.value = searchResults.value.length - 1;
    scrollActiveOptionIntoView();
    return;
  }

  activeIndex.value = nextIndex % searchResults.value.length;
  scrollActiveOptionIntoView();
};

const handleKeydown = event => {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveActive(1);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveActive(-1);
    return;
  }

  if (event.key === 'Enter' && activeIndex.value >= 0 && searchResults.value[activeIndex.value]) {
    event.preventDefault();
    handleSelectUser(searchResults.value[activeIndex.value]);
    return;
  }

  if (event.key === 'Escape') {
    activeIndex.value = -1;
    clearSearch();
  }
};

watch(searchResults, results => {
  optionRefs.value = [];

  if (!results.length || activeIndex.value >= results.length) {
    activeIndex.value = -1;
  }
});
</script>

<template>
  <div class="user-search-input">
    <label :for="inputId" class="tray-form__label">{{ label }}</label>
    <div class="user-search-input__anchor">
      <input
        :id="inputId"
        v-model="searchQuery"
        type="text"
        class="tray-form__input user-search-input__field"
        :class="{ 'tray-form__input--error': error }"
        :placeholder="placeholder"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="hasPopup"
        :aria-controls="popupId"
        :aria-activedescendant="activeOptionId"
        :aria-describedby="describedBy"
        :aria-invalid="error ? 'true' : undefined"
        @input="handleInput"
        @keydown="handleKeydown"
      />
      <p v-if="error" :id="errorId" class="tray-form__field-error" role="alert">{{ error }}</p>

      <ul
        v-if="searchResults.length"
        :id="listboxId"
        class="user-search-input__popup user-search-input__results"
        role="listbox"
      >
        <li
          v-for="(user, index) in searchResults"
          :id="optionId(index)"
          :ref="element => setOptionRef(element, index)"
          :key="user.uuid"
          class="user-search-input__result"
          :class="{ 'user-search-input__result--active': index === activeIndex }"
          role="option"
          :aria-selected="index === activeIndex"
          @mousedown.prevent="handleSelectUser(user)"
          @mousemove="activeIndex = index"
        >
          <span class="user-search-input__name">{{ user.name }}</span>
          <span class="user-search-input__email">{{ user.email }}</span>
        </li>
      </ul>
      <div
        v-else-if="searching"
        :id="statusId"
        class="user-search-input__popup user-search-input__status"
        role="status"
      >
        Searching...
      </div>
      <div
        v-else-if="showNoResults"
        :id="statusId"
        class="user-search-input__popup user-search-input__status"
        role="status"
      >
        No users found
      </div>
      <div v-else :id="statusId" class="user-search-input__status user-search-input__status--sr">
        Type at least two characters to search.
      </div>
    </div>
  </div>
</template>

<style scoped>
.user-search-input {
  display: flex;
  flex-direction: column;
  gap: 6.5px;

  @media (--breakpoint-small-min) {
    gap: 7.5px;
  }
}

.user-search-input__anchor {
  position: relative;
}

.user-search-input__field {
  display: block;
}

.user-search-input__popup {
  position: absolute;
  z-index: var(--z-index-overlay);
  top: 100%;
  right: 0;
  left: 0;
  box-sizing: border-box;
  max-height: min(260px, 45vh);
  overflow-y: auto;
  margin: 0;
  border: 1.5px solid var(--gsd-mid-grey);
  border-top: 0;
  background: var(--gsd-white-base);

  @media (--breakpoint-small-min) {
    border-width: 2px;
    border-top: 0;
  }
}

.user-search-input__results {
  list-style: none;
  padding: 0;
}

.user-search-input__result {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--spacer-1);
  border-bottom: 1px solid var(--gsd-light-grey);
  cursor: pointer;
}

.user-search-input__result:hover,
.user-search-input__result--active {
  background: var(--gsd-light-grey);
}

.user-search-input__result:last-child {
  border-bottom: none;
}

.user-search-input__name {
  font-family: var(--font-sans);
  font-size: var(--typography-default-s-font-size);
  font-weight: 600;
}

.user-search-input__email {
  overflow-wrap: anywhere;
  color: var(--gsd-dark-grey-base);
  font-family: var(--font-sans);
  font-size: var(--typography-small-l-font-size);
}

.user-search-input__status {
  margin: 0;
  padding: var(--spacer-1);
  color: var(--gsd-dark-grey-base);
  font-family: var(--font-sans);
  font-size: var(--typography-small-l-font-size);
}

.user-search-input__status--sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
